import { useCallback, useEffect, useRef, useState } from 'react';

import { resolveActiveAppointmentId } from '@pages/Calendar/utils';
import type { CalendarAppointmentLayout } from '@pages/Calendar/utils';

import type { PointerEvent as ReactPointerEvent } from 'react';

interface PointerSample {
  xPx: number;
  yPx: number;
  gridWidthPx: number;
}

export interface AppointmentGridHoverHandlers {
  onPointerMove(event: ReactPointerEvent<HTMLDivElement>): void;
  onPointerLeave(): void;
}

export interface UseActiveAppointmentHoverResult {
  activeEventId: string | null;
  gridHoverHandlers: AppointmentGridHoverHandlers;
}

/**
 * Column-scoped hover arbitration for overlapping appointment previews.
 *
 * Instead of letting each preview elevate itself on `mouseenter` (which lets a
 * tall appointment cover a shorter overlapping one), the grid tracks the
 * pointer and a deterministic resolver decides which appointment is intended.
 * Only the resolved appointment id is returned so a single preview elevates,
 * keeping every overlapping appointment hoverable, clickable and draggable.
 *
 * Pointer moves are throttled with `requestAnimationFrame` and state is updated
 * only when the resolved appointment actually changes, avoiding re-renders on
 * every pointer movement.
 */
export const useActiveAppointmentHover = (
  layouts: CalendarAppointmentLayout[]
): UseActiveAppointmentHoverResult => {
  const [activeEventId, setActiveEventId] = useState<string | null>(null);

  const layoutsRef = useRef(layouts);
  layoutsRef.current = layouts;

  const activeEventIdRef = useRef<string | null>(activeEventId);
  activeEventIdRef.current = activeEventId;

  const pointerRef = useRef<PointerSample | null>(null);
  const frameRef = useRef<number | null>(null);

  const resolveFromPointer = useCallback(() => {
    frameRef.current = null;

    const sample = pointerRef.current;

    if (!sample) {
      return;
    }

    const nextActiveId = resolveActiveAppointmentId({
      pointerXPx: sample.xPx,
      pointerYPx: sample.yPx,
      gridWidthPx: sample.gridWidthPx,
      layouts: layoutsRef.current,
    });

    if (nextActiveId !== activeEventIdRef.current) {
      setActiveEventId(nextActiveId);
    }
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();

      pointerRef.current = {
        xPx: event.clientX - rect.left,
        yPx: event.clientY - rect.top,
        gridWidthPx: rect.width,
      };

      if (frameRef.current !== null) {
        return;
      }

      frameRef.current = requestAnimationFrame(resolveFromPointer);
    },
    [resolveFromPointer]
  );

  const onPointerLeave = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    pointerRef.current = null;

    if (activeEventIdRef.current !== null) {
      setActiveEventId(null);
    }
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    },
    []
  );

  return {
    activeEventId,
    gridHoverHandlers: { onPointerMove, onPointerLeave },
  };
};
